<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import {
    Button,
    DataTable,
    DataTableSkeleton,
    InlineNotification,
    Toolbar,
    ToolbarContent,
    ToolbarSearch,
    Tooltip,
  } from 'carbon-components-svelte';

  import type { InlineNotificationProps } from 'carbon-components-svelte/src/Notification/InlineNotification.svelte';

  import debounce from 'lodash/debounce';

  import CircleSolid16 from 'carbon-icons-svelte/lib/CircleSolid16';

  import LoadingButton from '../../../components/LoadingButton.svelte';
  import PassiveEntityTableModal from '../../../components/PassiveEntityTableModal.svelte';
  import JsonPassiveModal from '../../customer-builds/JsonPassiveModal.svelte';

  import { DataInventoryServiceIdToName } from '../../../../shared/constants';
  import { getSLATaskExecutionHistory, getSLATaskRaisedAlerts } from '../../../services';
  import { convertToLocalDisplayTime } from '../../../utils';

  import type { SlaRaisedAlertRecipient, SlaTaskExecutionHistoryEntry } from '../../../../shared/types/sla-task-types';

  export let slaTaskId: string;

  let data: SlaTaskExecutionHistoryEntry[] = [],
    isLoading = true,
    loadingRecipientsForMessageId: string | null = null,
    searchQuery = '',
    resultNotification: Required<Pick<InlineNotificationProps, 'title' | 'subtitle' | 'kind'>> | null = null;

  let breachDetailsModalData: {
      messageId: string;
      executedAt: string;
      breaches: SlaTaskExecutionHistoryEntry['breaches'];
    } | null = null,
    affectedGroupsModalData: { messageId: string; executedAt: string; groups: { id: string; name: string }[] } | null = null,
    raisedAlertModalData: { messageId: string; executedAt: string; recipients: SlaRaisedAlertRecipient[] } | null = null,
    logsModalData: {
      messageId: string;
      executedAt: string;
      upstreamLogs: SlaTaskExecutionHistoryEntry['upstreamLogs'];
    } | null = null;

  const groupActionLink = (group?: Record<string, unknown>): string => (group ? `/group/${group.id as string}` : '');
  const executionModalLabel = (executedAt: string, messageId: string): string =>
    `Executed at ${convertToLocalDisplayTime(executedAt)} (${messageId})`;
  const applySearch = debounce((value: string) => (searchQuery = value.trim().toLocaleLowerCase()), 250);

  async function loadExecutionHistory(): Promise<void> {
    try {
      data = await getSLATaskExecutionHistory(slaTaskId);
    } catch (err) {
      console.error('[SlaExecutionHistory] Failed to load execution history', err);
      resultNotification = {
        kind: 'error',
        title: 'Error:',
        subtitle: 'Failed to load execution history. Please try again later.',
      };
    } finally {
      isLoading = false;
    }
  }

  function affectedGroups(entry: SlaTaskExecutionHistoryEntry): { id: string; name: string }[] {
    const groupsById = new Map<string, { id: string; name: string }>();
    entry.breaches.forEach((breach) => breach.groups?.forEach((group) => groupsById.set(group.id, group)));
    return [...groupsById.values()];
  }

  function breachDetailRows(breaches: SlaTaskExecutionHistoryEntry['breaches']) {
    return breaches.map((breach) => ({
      id: `${breach.serviceId}-${breach.marketDay}`,
      service: DataInventoryServiceIdToName[breach.serviceId],
      marketDay: breach.marketDay,
      feeds: breach.feeds?.length ? breach.feeds.map((feed) => feed.name) : null,
      groups: breach?.groups?.length ? breach.groups.map((group) => group.name) : null,
    }));
  }

  function onSearchInput(event: Event): void {
    applySearch((event.target as HTMLInputElement).value);
  }

  function onSearchClear(): void {
    applySearch.cancel();
    searchQuery = '';
  }

  async function loadRaisedAlertRecipients(messageId: string, executedAt: string): Promise<void> {
    loadingRecipientsForMessageId = messageId;
    try {
      raisedAlertModalData = { messageId, executedAt, recipients: await getSLATaskRaisedAlerts(slaTaskId, messageId) };
    } catch (err) {
      console.error('[SlaExecutionHistory] Failed to load notified recipients', err);
      resultNotification = {
        kind: 'error',
        title: 'Error:',
        subtitle: 'Failed to load notified recipients. Please try again later.',
      };
    } finally {
      loadingRecipientsForMessageId = null;
    }
  }

  onMount(loadExecutionHistory);
  onDestroy(() => applySearch.cancel());

  $: rows = data.map((execution) => {
    const groups = affectedGroups(execution);

    const searchParts: (string | number)[] = [
      execution.messageId,
      convertToLocalDisplayTime(execution.executedAt),
      execution.violated ? 'breached' : 'no breach',
    ];
    for (const breach of execution.breaches) {
      searchParts.push(DataInventoryServiceIdToName[breach.serviceId], breach.marketDay);
      breach.feeds.forEach((feed) => searchParts.push(feed.name));
      breach.groups?.forEach((group) => searchParts.push(group.name));
    }

    return {
      ...execution,
      id: execution.messageId,
      breachCount: execution.breaches.length,
      status: execution.violated,
      affectedGroups: groups,
      affectedGroupCount: groups.length,
      logs: execution.upstreamLogs?.length ?? 0,
      searchIndex: searchParts.join(' ').toLocaleLowerCase(),
    };
  });

  $: filteredRows = searchQuery ? rows.filter((row) => row.searchIndex.includes(searchQuery)) : rows;
</script>

{#if resultNotification}
  <InlineNotification hideCloseButton lowContrast {...resultNotification} />
{:else if isLoading}
  <DataTableSkeleton showHeader={false} showToolbar={false} />
{:else if rows.length === 0}
  <p class="empty-state">No executions have been recorded for this task yet.</p>
{:else}
  <DataTable
    headers={[
      { key: 'executedAt', value: 'Execution time' },
      { key: 'status', value: 'Status' },
      { key: 'affectedGroupCount', value: 'Affected groups' },
      { key: 'notifiedUsersCount', value: 'Notified users' },
      { key: 'logs', value: '' },
    ]}
    rows={filteredRows}
    sortable
  >
    <span class="cell" slot="cell" let:cell let:row>
      {#if cell.key === 'executedAt'}
        <Tooltip triggerText={convertToLocalDisplayTime(cell.value)} direction="top" portalTooltip>
          <div class="tooltip-content">
            <strong>Execution ID:</strong>
            {row.messageId}
          </div>
        </Tooltip>
      {:else if cell.key === 'status'}
        <div class="status-cell" class:status-cell--breached={row.violated}>
          <CircleSolid16 style="flex-shrink: 0" />
          {#if row.violated}
            <Button
              class="padded"
              kind="ghost"
              size="small"
              disabled={!!loadingRecipientsForMessageId}
              on:click={() => (breachDetailsModalData = { messageId: row.messageId, executedAt: row.executedAt, breaches: row.breaches })}
            >
              {row.breachCount}
              {row.breachCount === 1 ? 'Breach' : 'Breaches'}
            </Button>
          {:else}
            <span class="padded">No Breach</span>
          {/if}
        </div>
      {:else if cell.key === 'logs'}
        <Button
          class="padded"
          kind="ghost"
          size="small"
          disabled={!row.logs || !!loadingRecipientsForMessageId}
          on:click={() => (logsModalData = { messageId: row.messageId, executedAt: row.executedAt, upstreamLogs: row.upstreamLogs })}
        >
          Logs
        </Button>
      {:else if cell.key === 'affectedGroupCount'}
        {#if row.affectedGroupCount}
          <Button
            class="padded"
            kind="ghost"
            size="small"
            disabled={!!loadingRecipientsForMessageId}
            on:click={() =>
              (affectedGroupsModalData = { messageId: row.messageId, executedAt: row.executedAt, groups: row.affectedGroups })}
          >
            {row.affectedGroupCount}
          </Button>
        {:else}
          —
        {/if}
      {:else if cell.key === 'notifiedUsersCount'}
        {#if row.notifiedUsersCount}
          <LoadingButton
            class="padded"
            kind="ghost"
            size="small"
            isLoading={loadingRecipientsForMessageId === row.messageId}
            disabled={!!loadingRecipientsForMessageId}
            on:click={() => loadRaisedAlertRecipients(row.messageId, row.executedAt)}
          >
            {row.notifiedUsersCount}
          </LoadingButton>
        {:else}
          —
        {/if}
      {:else if cell.display}
        {cell.display(cell.value, row)}
      {:else}
        {cell.value ?? ''}
      {/if}
    </span>
    <Toolbar>
      <ToolbarContent>
        <ToolbarSearch persistent on:input={onSearchInput} on:clear={onSearchClear} />
      </ToolbarContent>
    </Toolbar>
  </DataTable>
{/if}

{#if raisedAlertModalData}
  <PassiveEntityTableModal
    open
    modalHeading="Notified recipients"
    modalLabel={executionModalLabel(raisedAlertModalData.executedAt, raisedAlertModalData.messageId)}
    rows={raisedAlertModalData.recipients}
    on:close={() => (raisedAlertModalData = null)}
  />
{/if}

{#if affectedGroupsModalData}
  <PassiveEntityTableModal
    open
    modalHeading="Affected groups"
    modalLabel={executionModalLabel(affectedGroupsModalData.executedAt, affectedGroupsModalData.messageId)}
    rows={affectedGroupsModalData.groups}
    createActionLink={groupActionLink}
    on:close={() => (affectedGroupsModalData = null)}
  />
{/if}

{#if breachDetailsModalData}
  <PassiveEntityTableModal
    open
    exportable
    modalHeading="Breach details"
    modalLabel={executionModalLabel(breachDetailsModalData.executedAt, breachDetailsModalData.messageId)}
    rows={breachDetailRows(breachDetailsModalData.breaches)}
    on:close={() => (breachDetailsModalData = null)}
  />
{/if}

{#if logsModalData}
  <JsonPassiveModal
    open
    name="Logs"
    label={executionModalLabel(logsModalData.executedAt, logsModalData.messageId)}
    data={logsModalData.upstreamLogs}
    on:close={() => (logsModalData = null)}
  />
{/if}

<style>
  .empty-state {
    color: var(--cds-text-02);
    padding: 1rem 0;
  }

  .cell :global(.padded) {
    padding: 0 0.25rem;
    white-space: nowrap;
  }

  .tooltip-content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.75rem;
  }

  .status-cell {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }

  .status-cell :global(svg) {
    color: var(--cds-support-success, #24a148);
  }

  .status-cell--breached :global(svg) {
    color: var(--cds-support-error, #da1e28);
  }
</style>
